import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { chartOfAccountsApi, journalEntriesApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { BookOpen, Lightbulb } from 'lucide-react';

const TEMPLATES = [
  { id: 'salary', name: 'Pay Monthly Salaries', debit: '6000', credit: '1000', desc: 'Monthly salary payment — [Month Year]', hint: 'Debit Salaries & Wages, Credit Cash' },
  { id: 'rent', name: 'Record Rent Payment', debit: '6100', credit: '1000', desc: 'Office rent payment — [Month Year]', hint: 'Debit Rent & Premises, Credit Cash' },
  { id: 'utilities', name: 'Record Utilities Payment', debit: '6110', credit: '1000', desc: 'Electricity/water payment — [Month Year]', hint: 'Debit Utilities, Credit Cash' },
  { id: 'it_costs', name: 'IT / Software Payment', debit: '6300', credit: '1000', desc: 'IT infrastructure / software subscription — [Month Year]', hint: 'Debit IT & Technology, Credit Cash' },
  { id: 'insurance', name: 'Insurance Premium', debit: '6600', credit: '1000', desc: 'Insurance premium payment', hint: 'Debit Insurance, Credit Cash' },
  { id: 'audit_legal', name: 'Professional Services (Audit/Legal)', debit: '6500', credit: '1000', desc: 'Audit/Legal fees — [Provider Name]', hint: 'Debit Professional Services, Credit Cash' },
  { id: 'marketing', name: 'Marketing / Advertising Spend', debit: '6400', credit: '1000', desc: 'Marketing campaign — [Description]', hint: 'Debit Marketing, Credit Cash' },
  { id: 'ppe_purchase', name: 'Purchase Property/Equipment', debit: '1400', credit: '1000', desc: 'Purchase of [Asset Description]', hint: 'Debit PP&E, Credit Cash. Remember to set up monthly depreciation.' },
  { id: 'depreciation', name: 'Monthly Depreciation', debit: '6200', credit: '1401', desc: 'Monthly depreciation — [Asset Category]', hint: 'Debit Depreciation Expense, Credit Accumulated Depreciation' },
  { id: 'borrowing_receive', name: 'Receive Bank Borrowing', debit: '1000', credit: '2300', desc: 'Facility drawdown — [Lender Name]', hint: 'Debit Cash (money in), Credit Bank Borrowings (new liability)' },
  { id: 'borrowing_repay', name: 'Repay Bank Borrowing', debit: '2300', credit: '1000', desc: 'Loan repayment — [Lender Name]', hint: 'Debit Bank Borrowings (reduce liability), Credit Cash' },
  { id: 'invest_community', name: 'Invest in Community/Share Project', debit: '1202', credit: '1000', desc: 'Community investment — [Project Name]', hint: 'Debit Community & Share Investments, Credit Cash' },
  { id: 'invest_tbill', name: 'Purchase Government Bonds / T-Bills', debit: '1201', credit: '1000', desc: 'T-Bill purchase — [Maturity Date]', hint: 'Debit Gov Bonds & T-Bills, Credit Cash' },
  { id: 'invest_maturity', name: 'Investment Maturity / Sale', debit: '1000', credit: '1202', desc: 'Investment matured/sold — [Project Name]', hint: 'Debit Cash (money back), Credit Investment account' },
  { id: 'invest_income', name: 'Record Investment Income / Dividends', debit: '1000', credit: '4200', desc: 'Dividend/interest received — [Source]', hint: 'Debit Cash, Credit Investment Income' },
  { id: 'training', name: 'Staff Training Costs', debit: '6020', credit: '1000', desc: 'Training programme — [Description]', hint: 'Debit Training & Development, Credit Cash' },
  { id: 'security', name: 'Security Services', debit: '6120', credit: '1000', desc: 'Security services — [Provider]', hint: 'Debit Security Services, Credit Cash' },
  { id: 'regulatory', name: 'Regulatory / Compliance Costs', debit: '6950', credit: '1000', desc: 'SARB/FSCA compliance costs', hint: 'Debit Regulatory Costs, Credit Cash' },
];

const CreateJournalPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ debit_account: '', credit_account: '', amount: '', description: '', entry_date: new Date().toISOString().split('T')[0], reference: '' });
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showTemplates, setShowTemplates] = useState(true);

  useEffect(() => {
    chartOfAccountsApi.list().then(r => {
      const sorted = (r.data || []).sort((a, b) => (a.account_code || '').localeCompare(b.account_code || ''));
      setAccounts(sorted);
    }).catch(console.error);
  }, []);

  const applyTemplate = (templateId) => {
    const t = TEMPLATES.find(x => x.id === templateId);
    if (!t) return;
    setSelectedTemplate(templateId);
    setForm(prev => ({
      ...prev,
      debit_account: t.debit,
      credit_account: t.credit,
      description: t.desc,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.debit_account || !form.credit_account || !form.amount) {
      toast.error('Debit account, credit account, and amount are required');
      return;
    }
    if (form.debit_account === form.credit_account) {
      toast.error('Debit and credit accounts must be different');
      return;
    }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than zero'); return; }

    try {
      await journalEntriesApi.create({
        description: form.description || 'Manual journal entry',
        entries: [
          { account_code: form.debit_account, debit: amt, credit: 0 },
          { account_code: form.credit_account, debit: 0, credit: amt },
        ],
        reference_number: form.reference || undefined,
      });
      toast.success('Journal entry created — pending approval');
      setForm({ debit_account: '', credit_account: '', amount: '', description: '', entry_date: new Date().toISOString().split('T')[0], reference: '' });
      setSelectedTemplate('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create journal entry'); }
  };

  const activeTemplate = TEMPLATES.find(t => t.id === selectedTemplate);

  const renderAccountSelect = (label, field, testId) => (
    <div>
      <Label>{label} *</Label>
      <Select value={form[field]} onValueChange={v => setForm({...form, [field]: v})}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select account" /></SelectTrigger>
        <SelectContent>
          {accounts.map(a => (
            <SelectItem key={a.account_code || a.id} value={a.account_code}>
              <span className="font-mono text-xs mr-2">{a.account_code}</span>
              {a.account_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <AdminSubPage title="Create Journal Entry" breadcrumb={['Accounting', 'Create Journal Entry']} backPath="/dashboard/accounting">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-6 space-y-4" data-testid="je-form">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Entry Date *</Label><Input type="date" value={form.entry_date} onChange={e => setForm({...form, entry_date: e.target.value})} required data-testid="je-date" /></div>
            <div><Label>Reference Number</Label><Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="e.g. INV-2025-001" data-testid="je-ref" /></div>
          </div>

          {activeTemplate && (
            <div className="p-3 bg-blue-50 border border-blue-200 text-xs text-blue-800 flex items-start gap-2" data-testid="je-template-hint">
              <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{activeTemplate.name}</p>
                <p className="mt-0.5">{activeTemplate.hint}</p>
              </div>
            </div>
          )}

          {renderAccountSelect('Debit Account (money goes TO)', 'debit_account', 'je-debit')}
          {renderAccountSelect('Credit Account (money comes FROM)', 'credit_account', 'je-credit')}

          <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" min="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="e.g. 50000.00" required data-testid="je-amount" /></div>
          <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} placeholder="Describe the transaction..." data-testid="je-desc" /></div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-[10px] text-gray-400">Journal entries require maker-checker approval before posting to GL.</p>
            <Button type="submit" className="bg-[#1f1f1f] text-white" data-testid="je-submit">Submit Journal Entry</Button>
          </div>
        </form>

        <div className="space-y-4">
          <div className="bg-white border border-gray-200 p-4">
            <button onClick={() => setShowTemplates(!showTemplates)} className="flex items-center gap-2 text-sm font-semibold text-[#1f1f1f] w-full" data-testid="je-templates-toggle">
              <BookOpen className="w-4 h-4 text-[#C5A059]" />
              Quick Templates
              <span className="text-[10px] text-gray-400 ml-auto">{TEMPLATES.length} templates</span>
            </button>
            {showTemplates && (
              <div className="mt-3 space-y-1.5 max-h-[60vh] overflow-y-auto">
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t.id)}
                    className={`w-full text-left px-3 py-2 text-xs rounded border transition-colors ${selectedTemplate === t.id ? 'bg-[#1f1f1f] text-white border-[#1f1f1f]' : 'bg-gray-50 text-gray-700 border-gray-100 hover:border-[#C5A059] hover:bg-amber-50'}`}
                    data-testid={`je-template-${t.id}`}
                  >
                    <span className="font-medium">{t.name}</span>
                    <span className="block text-[10px] mt-0.5 opacity-70">{t.debit} → {t.credit}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3 text-[10px] text-amber-800">
            <p className="font-semibold mb-1">How it works</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Select a template or pick accounts manually</li>
              <li>Debit = where the value goes (expense, asset increase)</li>
              <li>Credit = where the value comes from (cash, liability increase)</li>
              <li>Entry goes to approval queue (maker-checker)</li>
              <li>Once approved, GL balances update automatically</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminSubPage>
  );
};
export default CreateJournalPage;
